/**
 * painelApi — chamadas à API do M10 (Painel e Administração) sobre o ApiClient.
 *
 * Rotas (CONTRATO CONGELADO — BINDING; respostas no envelope `{ data }`):
 *  - GET   /painel/totais    → { data: Totais }
 *  - GET   /painel/qualidade → { data: Qualidade }
 *  - GET   /painel/mapa      → { data: Mapa }
 *  - GET   /painel/sem-visita → { data: DocumentosSemVisita } (ADR-034)
 *  - GET   /painel/sem-visita/arquivos?motivo=&cursor=&limit= → { data: ArquivoSemVisita[], nextCursor }
 *  - POST  /reprocessamentos → { data: CriarReprocessamentoResponse }
 *  - GET   /reprocessamentos/{id} → { data: ReprocessamentoStatus }
 *  - GET   /reprocessamentos/estimativa?<filtros>|visitaId= → { data: EstimativaReprocessamento } (ADR-036)
 *  - GET   /reprocessamentos?cursor=&limit= → { data: ReprocessamentoResumoItem[], nextCursor } (ADR-036)
 *  - GET   /painel/sugestoes → { data: SugestoesReprocessamento } (ADR-036)
 *  - PATCH /config           → { data: ConfigWhiteLabel }
 *  - GET   /config           → { data: ConfigWhiteLabel } (público, boot da SPA)
 *
 * Nenhum dado pessoal é logado (RN-M11-001). O JWT é anexado pelo ApiClient.
 */

import { ApiClient } from './apiClient';
import {
  ArquivoSemVisita,
  AtualizarConfigInput,
  ConfigWhiteLabel,
  DocumentosSemVisita,
  MotivoSemVisita,
  PaginaArquivosSemVisita,
  CriarReprocessamentoInput,
  CriarReprocessamentoResponse,
  CriterioEstimativa,
  EstimativaReprocessamento,
  Mapa,
  Qualidade,
  ReprocessamentoResumoItem,
  ReprocessamentoStatus,
  SugestoesReprocessamento,
  Totais,
} from './painel-types';

/** Envelope `{ data }` do projeto (buildResponse). */
interface Envelope<T> {
  readonly data: T;
}

export class PainelApi {
  constructor(private readonly client: ApiClient) {}

  /** GET /painel/sem-visita — documentos sem visita, por motivo (ADR-034). */
  async semVisita(): Promise<DocumentosSemVisita> {
    const resp = await this.client.request<Envelope<DocumentosSemVisita>>('/painel/sem-visita');
    return resp.data;
  }

  /** GET /painel/sem-visita/arquivos — os documentos de um motivo, paginados. */
  async arquivosSemVisita(motivo: MotivoSemVisita, cursor?: string, limit = 50): Promise<PaginaArquivosSemVisita> {
    const query = new URLSearchParams({ motivo, limit: String(limit) });
    if (cursor) {
      query.set('cursor', cursor);
    }
    const resp = await this.client.request<{ data: readonly ArquivoSemVisita[]; nextCursor: string | null }>(
      `/painel/sem-visita/arquivos?${query.toString()}`,
    );
    return { itens: resp.data, nextCursor: resp.nextCursor };
  }

  /** GET /painel/totais — cards agregados (ADR-038: `evidencia` restringe as visitas a um tipo). */
  async totais(evidencia?: 'imagem' | 'planilha'): Promise<Totais> {
    const resp = await this.client.request<Envelope<Totais>>(`/painel/totais${evidencia ? `?evidencia=${evidencia}` : ''}`);
    return resp.data;
  }

  /** GET /painel/qualidade — percentuais por marcador + ranking (ADR-038: `evidencia` filtra). */
  async qualidade(evidencia?: 'imagem' | 'planilha'): Promise<Qualidade> {
    const resp = await this.client.request<Envelope<Qualidade>>(
      `/painel/qualidade${evidencia ? `?evidencia=${evidencia}` : ''}`,
    );
    return resp.data;
  }

  /** GET /painel/mapa — distribuição por UF + pontos (só coordenada válida).
   * Contrato congelado: req{—} (sem parâmetros). Filtragem por UF/município/
   * resultado é feita client-side sobre `porUf`/`pontos`. */
  async mapa(): Promise<Mapa> {
    const resp = await this.client.request<Envelope<Mapa>>('/painel/mapa');
    return resp.data;
  }

  /** POST /reprocessamentos — dispara reprocessamento (admin). */
  async criarReprocessamento(
    input: CriarReprocessamentoInput,
  ): Promise<CriarReprocessamentoResponse> {
    const resp = await this.client.request<Envelope<CriarReprocessamentoResponse>>(
      '/reprocessamentos',
      { method: 'POST', body: JSON.stringify(input) },
    );
    return resp.data;
  }

  /** GET /reprocessamentos/{id} — status de um reprocessamento (admin). */
  async statusReprocessamento(id: string): Promise<ReprocessamentoStatus> {
    const resp = await this.client.request<Envelope<ReprocessamentoStatus>>(
      `/reprocessamentos/${encodeURIComponent(id)}`,
    );
    return resp.data;
  }

  /** GET /reprocessamentos/estimativa — antes de confirmar: quantas, quantas a reler e o custo (ADR-036). */
  async estimarReprocessamento(criterio: CriterioEstimativa): Promise<EstimativaReprocessamento> {
    const query = new URLSearchParams('visitaId' in criterio ? { visitaId: criterio.visitaId } : { ...criterio.filtro });
    const s = query.toString();
    const resp = await this.client.request<Envelope<EstimativaReprocessamento>>(
      `/reprocessamentos/estimativa${s ? `?${s}` : ''}`,
    );
    return resp.data;
  }

  /** GET /reprocessamentos — os recentes (ADR-036). */
  async listarReprocessamentos(
    cursor?: string,
    limit = 20,
  ): Promise<{ itens: readonly ReprocessamentoResumoItem[]; nextCursor: string | null }> {
    const query = new URLSearchParams({ limit: String(limit) });
    if (cursor) {
      query.set('cursor', cursor);
    }
    const resp = await this.client.request<{ data: readonly ReprocessamentoResumoItem[]; nextCursor: string | null }>(
      `/reprocessamentos?${query.toString()}`,
    );
    return { itens: resp.data, nextCursor: resp.nextCursor };
  }

  /** GET /painel/sugestoes — quando vale reprocessar (ADR-036). */
  async sugestoes(): Promise<SugestoesReprocessamento> {
    const resp = await this.client.request<Envelope<SugestoesReprocessamento>>('/painel/sugestoes');
    return resp.data;
  }

  /** GET /config — white-label corrente (público, boot da SPA). */
  async obterConfig(): Promise<ConfigWhiteLabel> {
    const resp = await this.client.request<Envelope<ConfigWhiteLabel>>('/config');
    return resp.data;
  }

  /** PATCH /config — atualiza white-label (admin). */
  async atualizarConfig(input: AtualizarConfigInput): Promise<ConfigWhiteLabel> {
    const resp = await this.client.request<Envelope<ConfigWhiteLabel>>('/config', {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    return resp.data;
  }
}
